import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { apiError } from '../../../shared/api.js';
import { formatIQD, formatDateTime } from '../../../shared/format.js';
import { useMySalesToday } from '../hooks/useSales.js';

const TYPE_LABEL = {
  cafeteria: 'كافتيريا',
  toys: 'محل الألعاب',
  checkout: 'تسوية خروج',
  play_fee: 'رسوم دخول',
  manual: 'يدوي'
};

const STATUS = {
  paid: { label: 'مدفوعة', variant: 'success' },
  pending: { label: 'معلّقة', variant: 'warning' },
  partial: { label: 'جزئية', variant: 'warning' },
  cancelled: { label: 'ملغاة', variant: 'danger' }
};

/**
 * فواتير اليوم للكاشير نفسه — ومنها يُطلب إلغاء فاتورة.
 *
 * زر الإلغاء يظهر فقط لمن يملك `pos.refund` (نفس الصلاحية التي يحرسها
 * المسار في الخادم)، ولا يظهر إطلاقاً على فاتورة ملغاة — فلا طريق في الواجهة
 * لإلغاء نفس الفاتورة مرتين، والخادم يرفضها على كل حال.
 */
export function TodaySalesModal({ open, onClose, canCancel, onRequestCancel }) {
  const listQ = useMySalesToday({ enabled: open });
  const rows = listQ.data || [];

  if (!open) return null;

  const cancelledCount = rows.filter((r) => r.paymentStatus === 'cancelled').length;

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-center p-4"
      style={{ background: 'rgba(15,16,18,0.55)' }}
      onClick={onClose}
    >
      <div
        className="card w-[760px] max-w-full p-0 max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-h">
          <div className="flex-1">
            <h3 className="text-[15px] font-bold m-0">فواتير اليوم</h3>
            <div className="text-xs text-ink-3 mt-0.5">
              الفواتير المسجّلة باسمك اليوم
              {cancelledCount ? ` • ${cancelledCount} ملغاة` : ''}
            </div>
          </div>
          <button type="button" className="btn btn-ghost" onClick={() => listQ.refetch()} title="تحديث">
            <Icon name="clock" size={14} />
          </button>
          <button type="button" className="text-ink-3 hover:text-ink ms-2" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="card-b overflow-y-auto">
          {listQ.isLoading ? (
            <div className="text-center py-10 text-ink-3 text-[12.5px]">جاري التحميل...</div>
          ) : listQ.error ? (
            <div className="text-center py-10 text-red text-[12.5px]">{apiError(listQ.error)}</div>
          ) : !rows.length ? (
            <div className="text-center py-10 text-ink-3 text-[12.5px]">لا توجد فواتير مسجّلة باسمك اليوم</div>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="text-ink-2 text-[11.5px]">
                <tr className="border-b border-line">
                  <th className="p-2 text-start">الوقت</th>
                  <th className="p-2 text-start">الرقم</th>
                  <th className="p-2 text-start">النوع</th>
                  <th className="p-2 text-start">الطفل</th>
                  <th className="p-2 text-start">الإجمالي</th>
                  <th className="p-2 text-start">الحالة</th>
                  <th className="p-2 text-start">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => {
                  const st = STATUS[inv.paymentStatus] || { label: inv.paymentStatus, variant: 'muted' };
                  const isCancelled = inv.paymentStatus === 'cancelled';
                  return (
                    <tr
                      key={inv._id}
                      className="border-b border-line hover:bg-paper-2"
                      style={isCancelled ? { opacity: 0.65 } : undefined}
                    >
                      <td className="p-2 whitespace-nowrap" style={{ direction: 'ltr' }}>
                        {formatDateTime(inv.createdAt)}
                      </td>
                      <td className="p-2 font-mono text-[11.5px]">{inv.number}</td>
                      <td className="p-2">{TYPE_LABEL[inv.posType] || inv.posType}</td>
                      <td className="p-2">{inv.childId?.name || '—'}</td>
                      <td
                        className="p-2 font-bold"
                        style={isCancelled ? { textDecoration: 'line-through' } : undefined}
                      >
                        {formatIQD(inv.grandTotal || 0)}
                      </td>
                      <td className="p-2">
                        <Pill variant={st.variant}>{st.label}</Pill>
                      </td>
                      <td className="p-2">
                        {isCancelled ? (
                          <span className="text-ink-3 text-[11.5px]">
                            {inv.cancelReason ? `السبب: ${inv.cancelReason}` : 'ملغاة'}
                          </span>
                        ) : canCancel ? (
                          <button
                            type="button"
                            className="btn btn-sm text-red"
                            onClick={() => onRequestCancel?.(inv)}
                          >
                            <Icon name="trash" size={12} /> إلغاء
                          </button>
                        ) : (
                          <span className="text-ink-3 text-[11.5px]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="card-h border-b-0 border-t flex justify-end">
          <button type="button" className="btn" onClick={onClose}>إغلاق</button>
        </div>
      </div>
    </div>
  );
}
